import './AthleteCard.css';

import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  AthletePosition,
  AthleteStatus,
} from '../../../types/athlete';

interface AthleteCardProps {
  id: string;
  name: string;
  number: string;
  position: AthletePosition;
  returningstatus: AthleteStatus;
  isQuickAccess?: boolean;
  imageThumbnailUrl?: string;
  teamPlacement?: string;
  /** When omitted, the card is display-only (no navigation). */
  onClick?: (id: string) => void;
}

const AthleteCard: React.FC<AthleteCardProps> = ({
  id,
  name,
  number,
  position,
  returningstatus,
  isQuickAccess = false,
  imageThumbnailUrl,
  teamPlacement,
  onClick,
}) => {
  const initial = name.trim().charAt(0).toUpperCase() || '?';
  const imageSrc = imageThumbnailUrl?.trim() ?? '';
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [showImageSkeleton, setShowImageSkeleton] = useState(false);

  useEffect(() => {
    setImageLoaded(false);
    setImageFailed(false);
    setShowImageSkeleton(false);
  }, [imageSrc]);

  useEffect(() => {
    if (!imageSrc || imageLoaded || imageFailed) {
      setShowImageSkeleton(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setShowImageSkeleton(true);
    }, 120);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [imageSrc, imageLoaded, imageFailed]);

  useEffect(() => {
    if (!imageSrc) return;

    let cancelled = false;
    const probe = new Image();

    const markLoaded = (): void => {
      if (!cancelled) setImageLoaded(true);
    };

    probe.onload = markLoaded;
    probe.onerror = () => {
      if (!cancelled) setImageFailed(true);
    };
    probe.src = imageSrc;

    if (probe.complete && probe.naturalWidth > 0) {
      markLoaded();
    } else if (typeof probe.decode === 'function') {
      void probe.decode().then(markLoaded).catch(() => {});
    }

    return () => {
      cancelled = true;
      probe.onload = null;
      probe.onerror = null;
    };
  }, [imageSrc]);

  const handleImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>): void => {
    const img = e.currentTarget;
    if (typeof img.decode === 'function') {
      void img.decode().catch(() => {}).finally(() => {
        setImageLoaded(true);
      });
      return;
    }
    setImageLoaded(true);
  }, []);

  const handleImageError = useCallback((): void => {
    setImageFailed(true);
  }, []);

  const body = (
    <>
      <div className="athlete-card-media" aria-hidden="true">
        {imageSrc && !imageFailed ? (
          <>
            {showImageSkeleton ? (
              <div className="athlete-card-media-skeleton" />
            ) : null}
            <img
              src={imageSrc}
              alt=""
              className={[
                'athlete-card-media-img',
                imageLoaded ? 'athlete-card-media-img--visible' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onContextMenu={(event) => event.preventDefault()}
              onLoad={handleImageLoad}
              onError={handleImageError}
              loading="eager"
              decoding="async"
              draggable={false}
            />
          </>
        ) : (
          <span className="athlete-card-initial">{initial}</span>
        )}
      </div>

      <div className="athlete-card-main">
        <div className="athlete-card-title-row">
          <h3 className="athlete-card-name">{name}</h3>
          {isQuickAccess ? (
            <span className="athlete-card-quick-access-star" aria-hidden="true">★</span>
          ) : null}
        </div>
        <p className="athlete-card-number">#{number}</p>
        <div className="athlete-card-tags">
          {returningstatus ? (
            <span className="athlete-card-tag">{returningstatus}</span>
          ) : null}
          <span className="athlete-card-tag">{position}</span>
        </div>
      </div>

      {teamPlacement ? (
        <div className="athlete-card-team-badge" aria-label={`Team: ${teamPlacement}`}>
          <span className="athlete-card-team-badge-label">{teamPlacement}</span>
        </div>
      ) : null}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        className="athlete-card"
        onClick={() => onClick(id)}
        aria-label={`Score athlete ${name}, number ${number}`}
      >
        {body}
      </button>
    );
  }

  return (
    <div
      className="athlete-card athlete-card--static"
      aria-label={`Athlete ${name}, number ${number}`}
    >
      {body}
    </div>
  );
};

export default AthleteCard;
